import dayjs, { Dayjs } from 'dayjs';
import { genUUIDV4 } from '@frontend/string';
import { ApiOverviewStatsParams, FilterTypeEnum } from '../types';
import { generateChartsPayload } from '../utils';

export type Name = {
  firstName: string;
  lastName: string;
};

const firstNames = [
  'John',
  'Jane',
  'Mary',
  'James',
  'Emma',
  'Robert',
  'Linda',
  'Michael',
  'Patricia',
  'David',
  'Alice',
  'Bob',
  'Charlie',
  'Diana',
  'Eve',
  'Frank',
  'Grace',
  'Helen',
  'Ivan',
  'Julia',
];

const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Miller',
  'Davis',
  'Garcia',
  'Rodriguez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Perez',
  'Thompson',
  'White',
];

// This is an alternative set of names and none of the names are same as the first set
// Usually used when we need to show unique random names in the same scope (eg. practitioners vs patients) to avoid names confusion
const alternativeFirstNames = [
  'Katie',
  'Lily',
  'Mia',
  'Layla',
  'Mila',
  'Aria',
  'Zoe',
  'Ava',
  'Sophia',
  'Amelia',
  'Harper',
  'Ella',
  'Scarlett',
  'Emily',
  'Avery',
  'Abigail',
  'Luna',
  'Chloe',
  'Sofia',
  'Eleanor',
];

const alternativeLastNames = [
  'Adams',
  'Baker',
  'Clark',
  'Dixon',
  'Evans',
  'Foster',
  'Griffin',
  'Harris',
  'Irwin',
  'Jenkins',
  'Keller',
  'Lewis',
  'Mitchell',
  'Nelson',
  'Owens',
  'Parker',
  'Quinn',
  'Reed',
  'Stewart',
  'Turner',
];

const getRandomElement = <T>(array: T[]): T => array[Math.floor(Math.random() * array.length)] as T;

const generateRandomInt = (min?: number, max?: number): number => {
  min = Math.ceil(min || 0);
  max = Math.floor(max || 1);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const generateRandomFloat = (min?: number, max?: number): number => {
  return Math.random() * (max || 1) + (min || 0);
};

const generateRandomNumberString = (length: number): string => {
  let result = '';

  for (let i = 0; i < length; i++) {
    result += Math.floor(Math.random() * 10);
  }

  return result;
};

const generateRandomBirthDate = (): string => {
  const start = dayjs().subtract(100, 'year');
  const end = dayjs().subtract(18, 'year');
  const randomDate = start.add(Math.floor(Math.random() * end.diff(start, 'day')), 'day');
  return randomDate.format('YYYY-MM-DD');
};

const generateRandomDate = (isFutureDate?: boolean, withTime?: boolean): string => {
  const date = dayjs().add(Math.floor(Math.random() * 365), 'day');
  const formatString = withTime ? 'YYYY-MM-DD hh:mm A' : 'YYYY-MM-DD';
  return isFutureDate ? date.format(formatString) : date.subtract(365, 'day').format(formatString);
};

// Utility function to get random unique elements from an array
const getRandomUniqueElements = <T>(array: T[], count: number): T[] => {
  const shuffled = array.slice().sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

export const demoDataUtils = {
  alternativeFirstNames,
  alternativeLastNames,
  dummyLocationId: '00000000-0000-0000-0000-000000000000',
  firstNames,
  getRandomElement,
  generateRandomBirthDate,
  generateRandomDate,
  generateRandomInt,
  generateRandomFloat,
  generateRandomNumberString,
  lastNames,

  generateConsecutiveDates: (startDate: Date, count: number): Dayjs[] => {
    const dates = [];

    for (let i = 0; i < count; i++) {
      dates.push(dayjs(startDate).add(i, 'day'));
    }

    return dates;
  },

  getRandomElementNoMatch: <T>(array: T[], exclude?: T): T => {
    if (exclude == null) {
      return getRandomElement(array);
    }
    const filtered = array.filter((item) => item !== exclude);
    return filtered.length ? getRandomElement(filtered) : getRandomElement(array);
  },

  randomBooleanNoMatch: (exclude?: boolean) => {
    if (exclude == null) return Math.random() > 0.5;
    return !exclude;
  },

  getRandomDateAndTime: (isFutureDate?: boolean) => generateRandomDate(isFutureDate, true),
  getRandomUniqueElements,

  generateRandomUserNames: (count: number, useAletrnativeList?: boolean): Name[] => {
    const names: Name[] = [];
    const firstNamesList = useAletrnativeList ? alternativeFirstNames : firstNames;
    const lastNamesList = useAletrnativeList ? alternativeLastNames : lastNames;

    for (let i = 0; i < count; i++) {
      const firstName = firstNames[Math.floor(Math.random() * firstNamesList.length)] || '';
      const lastName = lastNames[Math.floor(Math.random() * lastNamesList.length)] || '';
      names.push({ firstName, lastName });
    }

    return names;
  },

  generateRandomUUID: (): string => `demo-${genUUIDV4()}`,

  generateRandomBoolean: () => {
    const values = [true, false];
    return values[generateRandomInt()];
  },

  generateRandomGender: () => {
    const genders = ['Female', 'Male'];
    return genders[generateRandomInt()];
  },

  generateRandomPhoneNumber: () => generateRandomNumberString(10),

  generateRandomIntArray: (count?: number) => {
    const array = [];
    const length = count || 10;

    for (let i = 0; i < length; i++) {
      array.push(generateRandomInt(5, 100));
    }

    return array;
  },
};

export const fakeApi = async <T>(value: T, delay = 200): Promise<T> => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(value), delay);
  });
};

export const getDemoServiceQualityChartStats = ({ includes, filters }: ApiOverviewStatsParams) => {
  const { filter: chartsFilter } = generateChartsPayload({ filters });

  const payload = {
    request: {
      filter: {
        ...chartsFilter,
        [FilterTypeEnum.FILTER_BY_LOCATION]: {
          locations: ['40417eee-943e-4b57-80d8-794b41676885'], // This is hardcoded location id for demodata
        },
      },
      includes: {
        opportunities: includes.opportunities,
        service_quality: includes.serviceQuality,
      },
      timezone: dayjs.tz.guess(),
    },
  };

  return payload;
};
